import { useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import CertificateCard from "../components/CertificateCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import {
  Twitter, Linkedin, Facebook, Link as LinkIcon,
  Download, Coins, TrendingUp, Sparkles, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { certificates } from "../lib/certificateData";
import { useAuth } from "../context/AuthContext";

export default function Certificate() {
  const { user } = useAuth();
  const [activeId, setActiveId] = useState(certificates[0].id);
  const active = useMemo(() => certificates.find(c => c.id === activeId), [activeId]);

  const shareText = `I just earned a Kindness Certificate on @NeighborNetAI — ${active.stats.livesTouched} people helped, ${active.stats.hours} volunteer hours, ${active.stats.meals} meals shared, ${active.stats.jobsCreated} jobs created. This is my new social currency.`;
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/certificate?id=${active.id}`
    : "";

  const share = (net) => {
    const enc = encodeURIComponent;
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
    };
    window.open(urls[net], "_blank", "width=600,height=560,noopener,noreferrer");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Shareable link copied!");
    } catch (e) {
      toast.error("Couldn't copy — try selecting the URL manually.");
    }
  };

  const downloadNote = () => {
    toast.success("Screenshot the certificate — or use browser Print → Save as PDF for a hi-res version.");
  };

  return (
    <div className="noise-bg">
      <PageHeader
        eyebrow="A new social currency"
        title="Your kindness, made shareable."
        description="Every act of help earns you a verified Kindness Certificate — a beautiful, portable record of the impact you've made. Because the world should celebrate contribution, not just wealth."
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-10">
        {/* Philosophy strip */}
        <div className="grid md:grid-cols-3 gap-4 mb-14">
          {[
            { icon: Coins, t: "Contribution > wealth", d: "We measure people by what they give, not what they own." },
            { icon: TrendingUp, t: "Verified & portable", d: "Every cert is signed by the platform. Take yours anywhere." },
            { icon: Trophy, t: "Recognized globally", d: "Employers, universities and communities honor NeighborNet certs." },
          ].map((v, i) => (
            <Card
              key={v.t}
              data-testid={`philosophy-${i}`}
              className={`fade-up stagger-${i + 1} rounded-2xl p-6 border-border/60 shadow-none bg-white/60`}
            >
              <div className="h-10 w-10 rounded-2xl bg-secondary/15 text-secondary flex items-center justify-center mb-3">
                <v.icon size={18} />
              </div>
              <div className="font-display font-semibold">{v.t}</div>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{v.d}</p>
            </Card>
          ))}
        </div>

        {/* Featured certificate */}
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 fade-up">
            <CertificateCard cert={active} />
          </div>

          <div className="lg:col-span-1 space-y-6 fade-up stagger-2">
            <Card className="rounded-3xl border-border/60 p-6 shadow-none">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={16} className="text-primary" />
                <h3 className="font-display font-semibold text-lg">Share this certificate</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Your contribution is worth celebrating. Post it, print it, or add it to your résumé.
              </p>

              <div className="grid grid-cols-3 gap-2 mb-3">
                <Button
                  onClick={() => share("linkedin")}
                  data-testid="share-linkedin"
                  className="rounded-full bg-[#0A66C2] hover:bg-[#0A66C2]/90 text-white h-11"
                >
                  <Linkedin size={16} />
                </Button>
                <Button
                  onClick={() => share("twitter")}
                  data-testid="share-twitter"
                  className="rounded-full bg-black hover:bg-black/85 text-white h-11"
                >
                  <Twitter size={16} />
                </Button>
                <Button
                  onClick={() => share("facebook")}
                  data-testid="share-facebook"
                  className="rounded-full bg-[#1877F2] hover:bg-[#1877F2]/90 text-white h-11"
                >
                  <Facebook size={16} />
                </Button>
              </div>

              <Button
                onClick={copyLink}
                data-testid="share-copy-link"
                variant="outline"
                className="w-full rounded-full h-11 border-border mb-2"
              >
                <LinkIcon size={14} className="mr-2" /> Copy shareable link
              </Button>
              <Button
                onClick={downloadNote}
                data-testid="share-download"
                variant="ghost"
                className="w-full rounded-full h-11 text-muted-foreground hover:bg-muted"
              >
                <Download size={14} className="mr-2" /> Download hi-res
              </Button>
            </Card>

            <Card className="rounded-3xl border-border/60 p-6 shadow-none bg-primary text-primary-foreground">
              <Badge className="bg-white/20 text-white border-0 rounded-full mb-3">Your impact this year</Badge>
              <div className="text-4xl font-display font-bold">
                {active.stats.impactScore.toLocaleString()}
              </div>
              <div className="text-sm text-primary-foreground/85 mt-1">Community Impact Score</div>
              <div className="mt-4 pt-4 border-t border-white/15 text-sm text-primary-foreground/85 leading-relaxed">
                You're in the top <span className="font-semibold text-white">8%</span> of contributors in {active.location.split(",")[0]}.
              </div>
            </Card>

            {user && (
              <Card className="rounded-3xl border-border/60 p-6 shadow-none">
                <h3 className="font-display font-semibold mb-2">Your next milestone</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Complete <span className="font-semibold text-foreground">3 more matches</span> to unlock your Silver Kindness Certificate.
                </p>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-secondary rounded-full" style={{ width: "72%" }} />
                </div>
                <div className="mt-2 text-xs text-muted-foreground">72% to Silver tier</div>
              </Card>
            )}
          </div>
        </div>

        {/* Community gallery */}
        <div className="mt-24">
          <div className="flex items-end justify-between mb-8">
            <div>
              <Badge className="bg-secondary/15 text-secondary border-0 rounded-full mb-3">Community Hall</Badge>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                This month's most kind neighbors.
              </h2>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {certificates.map((c, i) => (
              <button
                key={c.id}
                onClick={() => setActiveId(c.id)}
                data-testid={`cert-tile-${c.id}`}
                className={`fade-up stagger-${i + 1} text-left rounded-3xl border p-6 transition-all ${
                  activeId === c.id
                    ? "border-primary/40 bg-white shadow-md"
                    : "border-border/60 bg-white/60 hover:border-border hover:bg-white"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                    c.tier === "Gold" ? "bg-yellow-500/10 text-yellow-700"
                    : c.tier === "Silver" ? "bg-slate-400/10 text-slate-600"
                    : "bg-amber-500/10 text-amber-600"
                  }`}>
                    <Trophy size={10} /> {c.tier}
                  </div>
                  <div className="text-xs text-muted-foreground">{c.dateIssued.split(",")[0]}</div>
                </div>
                <div className="font-display text-xl font-bold">{c.recipient}</div>
                <div className="text-sm text-muted-foreground">{c.location}</div>
                <div className="mt-4 pt-4 border-t border-border/60 grid grid-cols-4 gap-2 text-center">
                  <div>
                    <div className="font-display font-bold">{c.stats.livesTouched}</div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">People</div>
                  </div>
                  <div>
                    <div className="font-display font-bold">{c.stats.hours}</div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Hours</div>
                  </div>
                  <div>
                    <div className="font-display font-bold">{c.stats.jobsCreated}</div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Jobs</div>
                  </div>
                  <div>
                    <div className="font-display font-bold">{c.stats.impactScore}</div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Score</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Manifesto CTA */}
        <div className="mt-24 rounded-[2rem] bg-gradient-to-br from-primary via-primary/95 to-primary/80 text-primary-foreground p-10 sm:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-secondary/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <Coins size={40} className="mx-auto mb-6" />
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05] text-balance max-w-3xl mx-auto">
              A new social currency for the century of cooperation.
            </h2>
            <p className="mt-5 text-primary-foreground/85 max-w-xl mx-auto leading-relaxed">
              What if the most valuable line on your résumé wasn't a job title — but the neighbors you helped, the meals you shared, the lives you touched?
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
